import React, { useState } from 'react';
import { isValidHex } from '../lib/stepperColors';

/**
 * Shared swatch + hex + clear colour field (Issue #480).
 *
 * Extracted from the two near-identical colour rows in FormBuilder
 * (Brand Color / Button Color) and reused by the workflow canvas
 * Settings → STEPPER panel for the three stepper state colours.
 *
 * Contract:
 *   - `value` is a hex string or null/undefined (unset).
 *   - `onChange` receives a valid hex string (lower-cased) or null — it is
 *     NEVER called with an invalid value. Typing a partial/invalid hex is
 *     held in local state (the text input shows it) until it becomes valid,
 *     so garbage never reaches styling persistence or style attributes.
 *   - Emptying the text input commits null.
 *   - The swatch always shows a concrete colour: the committed value, or
 *     `fallback` (the effective default) while unset.
 *
 * Free-text mode (`allowFreeText`): some colour props legitimately hold
 * non-hex values — e.g. the email builder's `background_color: 'transparent'`
 * (a registry default) or named colours. With `allowFreeText`, onChange
 * receives the raw trimmed text (empty → null) and the swatch shows the
 * value only when it is valid hex, falling back to `fallback` otherwise.
 * Typing is never held in draft state in this mode.
 *
 * Styling hooks: the defaults match FormBuilder's Tailwind idiom; pass
 * `swatchClass` / `inputClass` / `clearClass` / `rowClass` to adapt to
 * other panels (e.g. the inline-styled workflow canvas settings panel).
 */
const DEFAULT_ROW_CLASS = 'flex items-center gap-2';
const DEFAULT_SWATCH_CLASS = 'w-10 h-10 rounded border border-stone-200 cursor-pointer';
const DEFAULT_INPUT_CLASS = 'w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20';
const DEFAULT_CLEAR_CLASS = 'text-xs text-indigo-600 hover:underline whitespace-nowrap';

export default function ColorField({
    value,
    onChange,
    fallback = '#ffffff',
    allowClear = true,
    clearLabel = 'Clear',
    placeholder,
    swatchLabel = 'Pick colour',
    allowFreeText = false,
    rowClass = DEFAULT_ROW_CLASS,
    swatchClass = DEFAULT_SWATCH_CLASS,
    inputClass = DEFAULT_INPUT_CLASS,
    clearClass = DEFAULT_CLEAR_CLASS,
}) {
    const committed = isValidHex(value) ? value : null;
    // null draft = "not drafting" → show the committed value. A non-null
    // draft is text the user typed that isn't (yet) a valid hex. Free-text
    // mode never drafts — every keystroke commits.
    const [draft, setDraft] = useState(null);
    const shown = draft !== null ? draft : (allowFreeText ? (value || '') : (committed || ''));

    const commitText = raw => {
        const trimmed = raw.trim();
        if (trimmed === '') {
            setDraft(null);
            onChange(null);
            return;
        }
        if (isValidHex(trimmed)) {
            setDraft(null);
            onChange(trimmed.toLowerCase());
        } else if (allowFreeText) {
            setDraft(null);
            onChange(trimmed);
        } else {
            setDraft(raw);
        }
    };

    return (
        <div className={rowClass}>
            <input
                type="color"
                aria-label={swatchLabel}
                value={(committed || fallback).toLowerCase()}
                onChange={e => {
                    setDraft(null);
                    onChange(e.target.value);
                }}
                className={swatchClass}
            />
            <input
                type="text"
                value={shown}
                onChange={e => commitText(e.target.value)}
                placeholder={placeholder}
                className={inputClass}
                spellCheck={false}
            />
            {allowClear && committed && (
                <button
                    type="button"
                    onClick={() => {
                        setDraft(null);
                        onChange(null);
                    }}
                    className={clearClass}
                >
                    {clearLabel}
                </button>
            )}
        </div>
    );
}
